import { LinkButton } from "@digipost/dds-buttons-react";
import { useLanguage } from "@digipost/translate";
import { FC, useCallback, useEffect } from "react";

import { DigipostEntryPoint } from "../../../types/entrypoint";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../analytics/siteimprove";
import { useEntrypointContext } from "../EntrypointContext";
import Translated from "../infrastructure/components/Translated";
import { useRootPath } from "../infrastructure/hooks/useRootPath";
import { findUriIfExists } from "../infrastructure/link";
import styles from "./OAuth.module.less";
import { useIdportenLevelParam, useTokenParam } from "./oauth-utils";
import SpinnerContainer from "./spinnerContainer/SpinnerContainer";

export const OAuthIDportenAuthorize: FC<{
  shouldKeepIdportenSession: boolean;
}> = ({ shouldKeepIdportenSession }) => {
  const entrypoint: DigipostEntryPoint = useEntrypointContext();
  const { language } = useLanguage();
  const rootPath = useRootPath();

  const token = useTokenParam();
  const idportenLevel = useIdportenLevelParam();

  const doLogin = useCallback(async () => {
    const idpAuthUrl = findUriIfExists("login_idp", entrypoint.link);

    const returnUrl = shouldKeepIdportenSession
      ? `${rootPath}/mobileSuccess/${token}`
      : `${rootPath}/mobileSuccessWithIDportenLogout/${token}`;
    const returnUrlError = `${rootPath}/loginError`;

    const idpSecurityLevel = toIdpAuthLevel(idportenLevel);

    window.location.href =
      idpAuthUrl +
      `?prompt=login&provider=idporten&eidasLoa=${idpSecurityLevel}&rolle=PRIVAT&returnUrl=${returnUrl}&returnUrlError=${returnUrlError}&lang=${language}`;
  }, [entrypoint, token, shouldKeepIdportenSession]);

  useEffect(() => {
    doLogin();
  }, [doLogin]);

  return (
    <SpinnerContainer>
      <LinkButton
        className={styles.loginLink}
        onClick={() => {
          siteImproveEvent({
            action: shouldKeepIdportenSession
              ? SiteImproveActions.LOGIN_WITHOUT_IDPORTEN_LOGOUT
              : SiteImproveActions.LOGIN_WITH_IDPORTEN_LOGOUT,
            category: SiteImproveCategories.LOGIN_PRIVATE_USER,
            label: "knapp for å logge inn i Digipost",
          });
          doLogin();
        }}
      >
        <Translated textKey={"oauth.if_no_redirect"} />
      </LinkButton>
    </SpinnerContainer>
  );
};

function toIdpAuthLevel(idportenLevel: string): "SUBSTANTIAL" | "HIGH" {
  switch (idportenLevel) {
    case "IDPORTEN_NIVAA_3":
      return "SUBSTANTIAL";
    case "IDPORTEN_NIVAA_4":
      return "HIGH";
    default:
      throw new Error("Unexpected ID-porten security level: " + idportenLevel);
  }
}
